import { Badge } from "../../../components/ui/badge";
import { DASH, FEATURE_LABELS, formatNumber, humanize, levelTone } from "../format";
import type { CxResult, FeatureKey } from "../types";
import { Block, Empty, Quote, Stat, Unavailable, yesNo, type TurnRef } from "./shared";

const featureName = (key: string) => FEATURE_LABELS[key as FeatureKey] ?? humanize(key);

export function CxPanel({ result, turnRef }: { result: CxResult; turnRef: TurnRef }) {
  const missing = result.inputs.missing;
  return (
    <div className="space-y-5">
      {!result.availability.llm ? <Unavailable>The language model was unavailable, so only measured signals were used.</Unavailable> : null}
      {missing.length ? (
        <Unavailable>Not checked, because these did not run on this call: {missing.map(featureName).join(", ")}.</Unavailable>
      ) : null}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="CX score" value={formatNumber(result.cx_score)} hint="out of 100" />
        <Stat label="Customer effort" value={formatNumber(result.effort?.score)} hint="higher is harder" />
        <Stat
          label="Escalation risk"
          value={result.escalation_risk?.level ? <Badge tone={levelTone(result.escalation_risk.level)}>{humanize(result.escalation_risk.level)}</Badge> : DASH}
          hint={`score ${formatNumber(result.escalation_risk?.score)}`}
        />
        <Stat label="Follow-up needed" value={yesNo(result.follow_up_required)} />
      </div>

      {result.summary ? <p className="text-sm text-ink-2">{result.summary}</p> : null}

      <div className="grid gap-5 md:grid-cols-2">
        <Block title="Satisfaction">
          <p className="text-sm text-ink">{humanize(result.satisfaction?.level)}</p>
          {result.satisfaction?.indicators.length ? (
            <ul className="space-y-1 text-sm text-ink-2">
              {result.satisfaction.indicators.map((s, i) => (
                <li key={i}>
                  <Badge tone={s.polarity === "negative" ? "danger" : s.polarity === "positive" ? "success" : "neutral"}>{humanize(s.polarity)}</Badge> {s.signal}
                  <Quote text={s.quote} />
                </li>
              ))}
            </ul>
          ) : null}
        </Block>
        <Block title="Effort signals">
          {result.effort?.signals.length ? (
            <ul className="space-y-1 text-sm text-ink-2">
              {result.effort.signals.map((s, i) => <li key={i}>{s.signal}<Quote text={s.quote} turn={turnRef(s.turn_index)} /></li>)}
            </ul>
          ) : <Empty>None.</Empty>}
        </Block>
        <Block title="Unresolved concerns">
          {result.unresolved_concerns.length ? (
            <ul className="space-y-1 text-sm text-ink-2">
              {result.unresolved_concerns.map((c, i) => <li key={i}>{c.concern}<Quote text={c.quote} /></li>)}
            </ul>
          ) : <Empty>None.</Empty>}
        </Block>
        <Block title="Friction points">
          {result.friction_points.length ? (
            <ul className="space-y-1 text-sm text-ink-2">
              {result.friction_points.map((f, i) => <li key={i}>{f.point}<Quote text={f.quote} turn={turnRef(f.turn_index)} /></li>)}
            </ul>
          ) : <Empty>None.</Empty>}
        </Block>
      </div>

      {result.escalation_risk?.factors.length ? (
        <Block title="Risk factors">
          <ul className="list-disc space-y-0.5 pl-5 text-sm text-ink-2">
            {result.escalation_risk.factors.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        </Block>
      ) : null}

      <Block title="Suggestions">
        {result.suggestions.length ? (
          <ul className="divide-y divide-line rounded-xl border border-line">
            {result.suggestions.map((s, i) => (
              <li key={i} className="space-y-1 px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={levelTone(s.priority)}>{humanize(s.priority)}</Badge>
                  <span className="text-xs text-ink-3">
                    for the {s.audience === "ai_agent" ? "AI agent" : "human team"}
                    {s.source_feature ? ` · from ${featureName(s.source_feature)}` : ""}
                    {s.evidence_count ? ` · ${s.evidence_count} signal${s.evidence_count === 1 ? "" : "s"}` : ""}
                  </span>
                </div>
                <p className="text-ink">{s.action}</p>
              </li>
            ))}
          </ul>
        ) : <Empty>None.</Empty>}
      </Block>
    </div>
  );
}
